import type { BookingResponse } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { useTranslation } from 'react-i18next';
import { SectionCard } from './SectionCard';

type Preparation = Pick<
  BookingResponse,
  | 'cleaningPreparationState'
  | 'availableBedCount'
  | 'availableSofaBedCount'
  | 'requestedBedCount'
  | 'requestedSofaBedCount'
>;

interface Props {
  preparation: Preparation;
}

const stateCopy = {
  NotConfigured: {
    labelKey: 'cleaningPreparation.booking.states.notConfigured.label',
    descriptionKey: 'cleaningPreparation.booking.states.notConfigured.description',
    tone: 'bg-line/70 text-ink-soft',
  },
  NotSpecified: {
    labelKey: 'cleaningPreparation.booking.states.notSpecified.label',
    descriptionKey: 'cleaningPreparation.booking.states.notSpecified.description',
    tone: 'bg-amber/15 text-ink',
  },
  Complete: {
    labelKey: 'cleaningPreparation.booking.states.complete.label',
    descriptionKey: 'cleaningPreparation.booking.states.complete.description',
    tone: 'bg-sage/15 text-sage',
  },
  LegacyConflict: {
    labelKey: 'cleaningPreparation.booking.states.legacyConflict.label',
    descriptionKey: 'cleaningPreparation.booking.states.legacyConflict.description',
    tone: 'bg-sealwax/10 text-sealwax',
  },
} as const;

export function CleaningPreparationCard({ preparation }: Props) {
  const { t } = useTranslation();
  const state = preparation.cleaningPreparationState;
  if (!state) return null;
  const copy = stateCopy[state];

  return (
    <SectionCard
      icon="Sparkle"
      title={t('cleaningPreparation.title')}
      tint="accent"
      className="rounded-none border-0 p-6 shadow-none"
    >
      <div>
        <span
          className={cn(
            'inline-flex max-w-full break-words rounded-full px-2.5 py-1 text-xs font-semibold',
            copy.tone,
          )}
        >
          {t(copy.labelKey)}
        </span>
        <p className="text-ink-soft mt-2 text-sm leading-relaxed">{t(copy.descriptionKey)}</p>
      </div>
      <dl className="mt-4 grid min-w-0 gap-3 sm:grid-cols-2">
        <PreparationQuantity
          label={t('cleaningPreparation.beds')}
          requested={preparation.requestedBedCount}
          available={preparation.availableBedCount}
          quantityCaption={t('cleaningPreparation.quantityCaption')}
        />
        <PreparationQuantity
          label={t('cleaningPreparation.sofaBeds')}
          requested={preparation.requestedSofaBedCount}
          available={preparation.availableSofaBedCount}
          quantityCaption={t('cleaningPreparation.quantityCaption')}
        />
      </dl>
    </SectionCard>
  );
}

function PreparationQuantity({
  label,
  requested,
  available,
  quantityCaption,
}: {
  label: string;
  requested?: number | null;
  available?: number | null;
  quantityCaption: string;
}) {
  return (
    <div className="bg-card-alt min-w-0 rounded-lg p-3">
      <dt className="text-ink-soft break-words text-xs font-semibold">{label}</dt>
      <dd className="text-ink mt-1 font-serif text-xl">
        {requested ?? '–'} / {available ?? '–'}
      </dd>
      <dd className="text-ink-faint mt-0.5 break-words text-[11px]">{quantityCaption}</dd>
    </div>
  );
}
